/**
 * `tags` — `TagsValue` (`specs/M2-tier2-column-types.md` §5.2, `[09§A.7 tags]`, `[cvs§2 tags]`).
 *
 * The second busiest tier-2 type here: **1,793 populated cells** and 54 account tags `[fum§8]`.
 *
 * `value` is `{"tag_ids":[859962,861376,…]}` — ids only, **integers, not id-strings**
 * (`tag_ids: [Int!]!`), with no `changed_at` on any of the sampled cells. The names and colours
 * come from the account-level `tags` table (`packages/db` `tags` / `board_tags`, migration
 * 0000), which is why `toText` needs a resolver: `ctx.resolveTagName(id)`.
 *
 * `text` is the tag **names joined with `", "` in `tag_ids` order** — measured:
 * `{"tag_ids":[923768,925475,1724989,923769]}` → `"2019New, Kids, Clothing, Evergreen"`.
 * A tag id with no row resolves to nothing and is **skipped** in the text while staying in the
 * value, which is what monday does with a tag deleted account-wide.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql, isBlankOnly, sqlFragment } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const tagsValueSchema = z.looseObject({
    tag_ids: z.array(z.number().int()),
});
import { tagsFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** `settings` is `{}` — *"tags are account-level"* `[09§A.7 tags]`. */
export const tagsSettingsSchema = z.looseObject({ hide_footer: z.boolean().optional() });
function coerceIds(raw) {
    if (raw === null || raw === undefined)
        return null;
    const list = Array.isArray(raw)
        ? raw
        : typeof raw === 'object'
            ? (raw.tag_ids ?? raw.tagIds)
            : typeof raw === 'string'
                ? raw.split(',')
                : undefined;
    if (list === null || list === undefined)
        return null;
    if (!Array.isArray(list))
        throw new ColumnValueError('tags', '"tag_ids" must be an array');
    const ids = [];
    for (const entry of list) {
        if (entry === null || entry === undefined || entry === '')
            continue;
        const id = typeof entry === 'number' ? entry : Number(String(entry).trim());
        if (!Number.isInteger(id))
            throw new ColumnValueError('tags', `"${String(entry)}" is not a tag id`);
        // monday keeps a tag once even if it is sent twice.
        if (!ids.includes(id))
            ids.push(id);
    }
    return ids.length === 0 ? null : ids;
}
function nameOf(id, ctx) {
    return ctx?.resolveTagName?.(id);
}
/**
 * The tag **names**, `", "`-joined in `tag_ids` order.
 *
 * The names are not in the cell — they live in the account-level `tags` table — so a caller
 * that supplies no `resolveTagName` cannot compute them. In that one case the rendering the
 * importer preserved in `cell_values.text_value` is used (`ctx.storedText`), which is the same
 * fall-back the `unsupported` passthrough makes and is the only correct answer available: the
 * alternative is to serve `""` for a populated cell. A caller **with** a resolver always wins,
 * so a stale shadow can never override a real rendering.
 */
function renderText(value, ctx) {
    if (value === null)
        return '';
    const names = value.tag_ids
        .map((id) => nameOf(id, ctx))
        .filter((name) => name !== undefined && name !== '');
    if (names.length > 0)
        return names.join(', ');
    if (ctx?.resolveTagName === undefined && typeof ctx?.storedText === 'string')
        return ctx.storedText;
    return '';
}
export const tagsColumn = defineColumnType({
    type: 'tags',
    title: 'Tags',
    description: 'Account-wide tags shared by every board.',
    apiFragmentName: 'TagsValue',
    legacyTypeName: 'tag',
    idPrefix: 'tags',
    valueSchema: tagsValueSchema,
    settingsSchema: tagsSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: tagsSettingsSchema,
    writable: true,
    /** *"does not support `change_simple_column_value`"* `[09§A.7 tags]`. */
    supportsSimpleValue: false,
    /** `tags` also accepts `{}` `[09§A.7 rule 4]`. */
    clearForms: ['null', 'empty-object'],
    filterConditions: tagsFilterConditions,
    filterOperators: operatorsOf(tagsFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        const ids = coerceIds(raw);
        return ids === null ? null : { tag_ids: ids };
    },
    toText(value, _settings, ctx) {
        return renderText(value, ctx);
    },
    toSortKeys(value, _settings, ctx) {
        return { text: textShadow(renderText(value, ctx)), number: null, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        // Order is not semantic: the picker appends, monday re-renders in id order.
        const left = [...a.tag_ids].sort((x, y) => x - y);
        const right = [...b.tag_ids].sort((x, y) => x - y);
        return left.length === right.length && left.every((id, index) => id === right[index]);
    },
    toActivityValue(value) {
        return value === null ? null : { tag_ids: [...value.tag_ids] };
    },
    fromMondayValue(raw) {
        const ids = coerceIds(raw);
        return ids === null ? null : { tag_ids: ids };
    },
    toMondayValue(value) {
        return value === null ? null : { tag_ids: [...value.tag_ids] };
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return tagsSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value, _settings, ctx) {
        const ids = value?.tag_ids ?? [];
        return {
            tag_ids: [...ids],
            // `Tag { id name color }`; ids are **strings** in the fragment `[cvs§2 tags]`. `name` is
            // `String!`, so the `/v2` read path resolves it from the `tags` table; `resolveTagName`
            // is the offline fall-back and an unknown id keeps monday's own id-only shape.
            tags: ids.map((id) => ({ id: String(id), name: nameOf(id, ctx) ?? null })),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Facet by tag **id** — a multi-tag cell is in several buckets, like `dropdown`. */
    bucketsOf(value, _settings, ctx) {
        return (value?.tag_ids ?? []).map((id) => ({
            key: id,
            label: ctx?.nameOf?.(id) ?? `#${String(id)}`,
        }));
    },
    /**
     * `any_of` compares tag **ids** inside the stored array, so it is a JSON containment test
     * against the GIN index, exactly like `dropdown`'s `[B5§5.3]`.
     */
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value IS NULL)`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.value IS NOT NULL`);
            case 'any_of':
            case 'not_any_of': {
                if (isBlankOnly(ctx.compareValues)) {
                    return ctx.operator === 'any_of'
                        ? sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value IS NULL)`)
                        : sqlFragment(`${cell}.value IS NOT NULL`);
                }
                const ids = ctx.compareValues.map(Number).filter((id) => Number.isInteger(id));
                if (ids.length === 0)
                    return null;
                const test = ids
                    .map(() => `${cell}.value -> 'tag_ids' @> ?::jsonb`)
                    .join(' OR ');
                const params = ids.map((id) => JSON.stringify([id]));
                return ctx.operator === 'any_of'
                    ? sqlFragment(`(${test})`, params)
                    : sqlFragment(`(${cell}.item_id IS NULL OR NOT (${test}))`, params);
            }
            default:
                return defaultFilterSql('text', ctx);
        }
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map